// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Workflow Engine Type Definitions
// User-configurable workflows: approvals, reviews, tasks,
// signatures, notifications, escalation
// ═══════════════════════════════════════════════════════════════

// ── Step types ──────────────────────────────────────────────

export type WorkflowStepType =
  | 'approval'       // requires approve/reject
  | 'review'         // requires review confirmation
  | 'task'           // creates a task for assignee
  | 'signature'      // requires typed/drawn signature
  | 'notification'   // sends email/SMS, auto-completes
  | 'create_record'  // creates a record in another module
  | 'update_field'   // sets a field value
  | 'conditional';   // branch based on condition

export type AssigneeType = 'user' | 'role' | 'position' | 'supervisor' | 'field_value' | 'creator' | 'previous_assignee';

export type StepStatus = 'pending' | 'in_progress' | 'completed' | 'rejected' | 'skipped' | 'escalated';

export type StepOutcome = 'approved' | 'rejected' | 'signed' | 'completed' | 'condition_true' | 'condition_false' | '';

// ── Conditional routing ────────────────────────────────────

export type ConditionOperator =
  | 'equals' | 'not_equals' | 'contains'
  | 'greater_than' | 'less_than'
  | 'is_empty' | 'is_not_empty'
  // Delta operators — only meaningful on record_event_update triggers,
  // they compare current value to _previous in contextData.
  | 'changed' | 'changed_to' | 'changed_from';

export interface StepCondition {
  field: string;              // field key from source data
  fieldLabel?: string;        // human-readable label (for display)
  operator: ConditionOperator;
  value: string;              // comparison value (ignored for is_empty/is_not_empty)
}

export const CONDITION_OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  greater_than: 'is greater than',
  less_than: 'is less than',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  changed: 'changed (any change)',
  changed_to: 'changed to',
  changed_from: 'changed from',
};

export type InstanceStatus = 'active' | 'completed' | 'cancelled' | 'escalated' | 'paused';

export type TriggerType = 'field_change' | 'record_create' | 'manual';

export type FriendlyTrigger =
  | 'new_damage_assessment' | 'new_form_response' | 'new_volunteer'
  | 'shelter_at_capacity' | 'incident_activated' | 'business_registered'
  | 'supply_low_stock' | 'reentry_requested'
  | 'new_intake' | 'new_debris_request' | 'new_hotwash_observation'
  | 'shelter_opened' | 'survivor_checked_in' | 'new_pa_project' | 'rfr_approved'
  | 'new_hmgp_subapp' | 'hmgp_subapp_forwarded' | 'hmgp_project_awarded'
  | 'handoff_completed' | 'new_perimeter_closure' | 'new_geoops_zone'
  | 'training_expired' | 'calldown_low_response'
  // Generic table-event triggers — fire on any record create/update/delete
  // for any table registered in src/lib/workflowTables.ts
  | 'record_event_create' | 'record_event_update' | 'record_event_delete'
  | 'custom_trigger'
  | 'manual';

export const FRIENDLY_TRIGGERS: { value: FriendlyTrigger; label: string; module: string }[] = [
  // Respond
  { value: 'incident_activated', label: 'Incident is activated', module: 'incidents' },
  { value: 'new_damage_assessment', label: 'New damage assessment submitted', module: 'impact' },
  { value: 'new_intake', label: 'New individual intake registered', module: 'intake' },
  { value: 'shelter_opened', label: 'New shelter created', module: 'shelter' },
  { value: 'shelter_at_capacity', label: 'Shelter reaches 80% capacity', module: 'shelter' },
  { value: 'survivor_checked_in', label: 'Survivor checks in to shelter', module: 'shelter' },
  { value: 'supply_low_stock', label: 'Supply item hits reorder threshold', module: 'shelter' },
  { value: 'new_perimeter_closure', label: 'New road closure created', module: 'geoops' },
  { value: 'new_geoops_zone', label: 'New zone created in GeoOps', module: 'geoops' },
  { value: 'new_debris_request', label: 'New debris zone/request created', module: 'debris' },
  // Prepare
  { value: 'new_volunteer', label: 'New volunteer signs up', module: 'volunteer' },
  { value: 'new_hotwash_observation', label: 'New HotWash observation submitted', module: 'hotwash' },
  { value: 'handoff_completed', label: 'Shift handoff submitted', module: 'handoff' },
  { value: 'training_expired', label: 'Training certificate created', module: 'blackboard' },
  { value: 'calldown_low_response', label: 'Call-down response recorded', module: 'pace' },
  // Recover
  { value: 'business_registered', label: 'Business registers on MainStreet', module: 'mainstreet' },
  { value: 'reentry_requested', label: 'Re-entry pass issued', module: 'mainstreet' },
  { value: 'new_pa_project', label: 'New PA Grant project created', module: 'pagrants' },
  { value: 'rfr_approved', label: 'Reimbursement request approved', module: 'pagrants' },
  { value: 'new_hmgp_subapp', label: 'New HMGP subapplication created', module: 'pagrants' },
  { value: 'hmgp_subapp_forwarded', label: 'HMGP subapplication forwarded to FEMA', module: 'pagrants' },
  { value: 'hmgp_project_awarded', label: 'HMGP project awarded by FEMA', module: 'pagrants' },
  // Cross-module
  { value: 'new_form_response', label: 'New form response received', module: 'forms' },
  { value: 'record_event_create', label: 'When a record is CREATED in any table', module: '' },
  { value: 'record_event_update', label: 'When a record is UPDATED in any table', module: '' },
  { value: 'record_event_delete', label: 'When a record is DELETED in any table', module: '' },
  { value: 'custom_trigger', label: 'Custom Trigger (manual fire only)', module: '' },
  { value: 'manual', label: 'Manually started', module: '' },
];

// ── Trigger configuration metadata ────────────────────────
// Tells the template editor what additional UI to show per trigger type.
// Table keys reference TABLES from api.ts — duplicated here to avoid circular import.

export interface TriggerConfig {
  configType: 'form_picker' | 'auto' | 'none';
  table?: string;    // Ignatius table key auto-populated for 'auto' triggers
  module?: string;   // module auto-populated for 'auto' triggers
  description: string;
}

export const FRIENDLY_TRIGGER_CONFIG: Record<FriendlyTrigger, TriggerConfig> = {
  // Respond
  incident_activated:    { configType: 'auto', table: '7pyjexwg1', module: 'incidents', description: 'an incident is activated' },
  new_damage_assessment: { configType: 'auto', table: 'dxzn49tg5', module: 'impact', description: 'a new damage assessment is submitted' },
  new_intake:            { configType: 'auto', table: 'rduintoa7', module: 'intake', description: 'a new individual is registered at intake' },
  shelter_opened:        { configType: 'auto', table: 'x3m9nk7g8', module: 'shelter', description: 'a new shelter is created' },
  shelter_at_capacity:   { configType: 'auto', table: 'x3m9nk7g8', module: 'shelter', description: 'a shelter reaches 80% capacity' },
  survivor_checked_in:   { configType: 'auto', table: '28kh7ttq7', module: 'shelter', description: 'a survivor checks in to a shelter' },
  supply_low_stock:      { configType: 'auto', table: 'w6m5vdbq4', module: 'shelter', description: 'a supply item hits reorder threshold' },
  new_perimeter_closure: { configType: 'auto', table: '9qxvohjw6', module: 'geoops', description: 'a new road closure is created' },
  new_geoops_zone: { configType: 'auto', table: 'xaumufzq4', module: 'geoops', description: 'a new zone is created in GeoOps' },
  new_debris_request:    { configType: 'auto', table: 'lpjrim4q4', module: 'debris', description: 'a new debris zone/request is created' },
  // Prepare
  new_volunteer:         { configType: 'auto', table: 'nlkym2sw7', module: 'volunteer', description: 'a new volunteer signs up' },
  new_hotwash_observation: { configType: 'auto', table: '95jnhplq1', module: 'hotwash', description: 'a new HotWash observation is submitted' },
  handoff_completed:     { configType: 'auto', table: 'tohix2zw7', module: 'handoff', description: 'a shift handoff is submitted' },
  training_expired:      { configType: 'auto', table: 'xts8cwkw4', module: 'blackboard', description: 'a training certificate is created' },
  calldown_low_response: { configType: 'auto', table: 'qrnky6ew6', module: 'pace', description: 'a call-down response is recorded' },
  // Recover
  business_registered:   { configType: 'auto', table: '74h4jcpa8', module: 'mainstreet', description: 'a business registers on MainStreet' },
  reentry_requested:     { configType: 'auto', table: 'ewyddu8w2', module: 'mainstreet', description: 'a re-entry pass is issued' },
  new_pa_project:        { configType: 'auto', table: 'ql1b8dqq1', module: 'pagrants', description: 'a new PA Grant project is created' },
  rfr_approved:          { configType: 'auto', table: 'yuqknqww3', module: 'pagrants', description: 'a reimbursement request is approved' },
  new_hmgp_subapp:       { configType: 'auto', module: 'pagrants', description: 'a new HMGP subapplication is created' },
  hmgp_subapp_forwarded: { configType: 'auto', module: 'pagrants', description: 'an HMGP subapplication is forwarded to FEMA' },
  hmgp_project_awarded:  { configType: 'auto', table: 'ql1b8dqq1', module: 'pagrants', description: 'an HMGP project is awarded by FEMA' },
  // Cross-module
  new_form_response:     { configType: 'form_picker', module: 'forms', description: 'a form response is received' },
  record_event_create:   { configType: 'none', module: '', description: 'a record is created in the chosen table' },
  record_event_update:   { configType: 'none', module: '', description: 'a record is updated in the chosen table' },
  record_event_delete:   { configType: 'none', module: '', description: 'a record is deleted from the chosen table' },
  custom_trigger:        { configType: 'none', module: '', description: 'a user-defined custom event occurs (manual fire only)' },
  manual:                { configType: 'none', description: 'the workflow is started manually from the Inbox' },
};

// ── Editor-safe subsets (only types with working UI + execution) ──

export const EDITOR_STEP_TYPES: WorkflowStepType[] = [
  'approval', 'review', 'task', 'signature', 'notification', 'conditional',
];

export const EDITOR_ASSIGNEE_TYPES: { value: AssigneeType; label: string }[] = [
  { value: 'user', label: 'Specific User' },
  { value: 'role', label: 'Role' },
  { value: 'creator', label: 'Workflow Creator' },
  { value: 'previous_assignee', label: 'Previous Step Assignee' },
];

// ── Trigger field registry (for conditional step field picker) ──
// Maps each trigger to the fields available on its source record.
// For 'new_form_response', fields come dynamically from the form schema instead.

export interface TriggerFieldDef {
  key: string;
  label: string;
  options?: { value: string; label: string }[]; // predefined choices
}

export const TRIGGER_FIELD_REGISTRY: Partial<Record<FriendlyTrigger, TriggerFieldDef[]>> = {
  new_damage_assessment: [
    { key: 'assessmentType', label: 'Assessment Type', options: [{ value: 'preliminary', label: 'Preliminary' }, { value: 'detailed', label: 'Detailed' }] },
    { key: 'damageCategory', label: 'Damage Category', options: [{ value: 'affected', label: 'Affected' }, { value: 'minor', label: 'Minor' }, { value: 'major', label: 'Major' }, { value: 'destroyed', label: 'Destroyed' }] },
    { key: 'paCategory', label: 'PA Category', options: [{ value: 'A', label: 'A — Debris Removal' }, { value: 'B', label: 'B — Emergency Measures' }, { value: 'C', label: 'C — Roads & Bridges' }, { value: 'D', label: 'D — Water Control' }, { value: 'E', label: 'E — Buildings & Equipment' }, { value: 'F', label: 'F — Utilities' }, { value: 'G', label: 'G — Parks & Recreation' }] },
    { key: 'severity', label: 'Severity' },
    { key: 'address', label: 'Address' },
    { key: 'county', label: 'County' },
    { key: 'habitable', label: 'Habitable', options: [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] },
    { key: 'insured', label: 'Insured', options: [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] },
    { key: 'residenceType', label: 'Residence Type' },
    { key: 'assessorName', label: 'Assessor Name' },
  ],
  new_intake: [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'triage', label: 'Triage Level', options: [{ value: 'green', label: 'Green' }, { value: 'yellow', label: 'Yellow' }, { value: 'red', label: 'Red' }, { value: 'black', label: 'Black' }] },
    { key: 'status', label: 'Status', options: [{ value: 'active', label: 'Active' }, { value: 'discharged', label: 'Discharged' }, { value: 'transferred', label: 'Transferred' }] },
    { key: 'gender', label: 'Gender' },
    { key: 'intakeMethod', label: 'Intake Method', options: [{ value: 'manual', label: 'Manual' }, { value: 'scan', label: 'Scan' }, { value: 'transfer', label: 'Transfer' }] },
    { key: 'medicalNeeds', label: 'Medical Needs' },
    { key: 'notes', label: 'Notes' },
  ],
  shelter_opened: [
    { key: 'name', label: 'Shelter Name' },
    { key: 'shelterType', label: 'Shelter Type' },
    { key: 'capacity', label: 'Capacity' },
    { key: 'status', label: 'Status', options: [{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }, { value: 'standby', label: 'Standby' }] },
    { key: 'isAda', label: 'ADA Accessible', options: [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] },
    { key: 'petFriendly', label: 'Pet Friendly', options: [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] },
    { key: 'address', label: 'Address' },
    { key: 'operatingOrg', label: 'Operating Organization' },
  ],
  shelter_at_capacity: [
    { key: 'name', label: 'Shelter Name' },
    { key: 'capacity', label: 'Capacity' },
    { key: 'currentPopulation', label: 'Current Population' },
    { key: 'shelterType', label: 'Shelter Type' },
    { key: 'status', label: 'Status', options: [{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }, { value: 'standby', label: 'Standby' }] },
  ],
  survivor_checked_in: [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'gender', label: 'Gender' },
    { key: 'status', label: 'Status', options: [{ value: 'checked_in', label: 'Checked In' }, { value: 'discharged', label: 'Discharged' }, { value: 'transferred', label: 'Transferred' }, { value: 'pre-registered', label: 'Pre-Registered' }] },
    { key: 'isMinor', label: 'Is Minor', options: [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] },
    { key: 'specialNeeds', label: 'Special Needs' },
    { key: 'county', label: 'County' },
    { key: 'city', label: 'City' },
  ],
  supply_low_stock: [
    { key: 'itemName', label: 'Item Name' },
    { key: 'category', label: 'Category', options: [{ value: 'bedding', label: 'Bedding' }, { value: 'food', label: 'Food' }, { value: 'water', label: 'Water' }, { value: 'hygiene', label: 'Hygiene' }, { value: 'medical', label: 'Medical' }, { value: 'cleaning', label: 'Cleaning' }, { value: 'other', label: 'Other' }] },
    { key: 'quantity', label: 'Quantity' },
    { key: 'reorderThreshold', label: 'Reorder Threshold' },
    { key: 'unit', label: 'Unit' },
  ],
  new_volunteer: [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'org', label: 'Organization' },
    { key: 'rolePref', label: 'Role Preference' },
    { key: 'status', label: 'Status', options: [{ value: 'pending', label: 'Pending' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }] },
    { key: 'certs', label: 'Certifications' },
    { key: 'skills', label: 'Skills' },
  ],
  new_hotwash_observation: [
    { key: 'title', label: 'Title' },
    { key: 'tier', label: 'Tier', options: [{ value: 'sustain', label: 'Sustain' }, { value: 'improve', label: 'Improve' }, { value: 'critical', label: 'Critical' }] },
    { key: 'category', label: 'Category', options: [{ value: 'operations', label: 'Operations' }, { value: 'logistics', label: 'Logistics' }, { value: 'planning', label: 'Planning' }, { value: 'finance', label: 'Finance' }, { value: 'command', label: 'Command' }, { value: 'communications', label: 'Communications' }, { value: 'other', label: 'Other' }] },
    { key: 'phase', label: 'Phase', options: [{ value: 'preparedness', label: 'Preparedness' }, { value: 'response', label: 'Response' }, { value: 'recovery', label: 'Recovery' }, { value: 'mitigation', label: 'Mitigation' }] },
    { key: 'description', label: 'Description' },
    { key: 'submittedBy', label: 'Submitted By' },
    { key: 'isAnonymous', label: 'Is Anonymous', options: [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] },
  ],
  handoff_completed: [
    { key: 'positionName', label: 'Position Name' },
    { key: 'fromShift', label: 'From Shift', options: [{ value: 'day', label: 'Day' }, { value: 'night', label: 'Night' }] },
    { key: 'toShift', label: 'To Shift', options: [{ value: 'day', label: 'Day' }, { value: 'night', label: 'Night' }] },
    { key: 'shiftDate', label: 'Shift Date' },
    { key: 'senderName', label: 'Sender Name' },
  ],
  business_registered: [
    { key: 'name', label: 'Business Name' },
    { key: 'sectorType', label: 'Sector Type' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'zip', label: 'ZIP Code' },
    { key: 'opStatus', label: 'Operating Status', options: [{ value: 'pending', label: 'Pending' }, { value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }, { value: 'damaged', label: 'Damaged' }] },
    { key: 'insurance', label: 'Has Insurance', options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] },
    { key: 'primaryContact', label: 'Primary Contact' },
  ],
  reentry_requested: [
    { key: 'orgName', label: 'Organization Name' },
    { key: 'code', label: 'Pass Code' },
    { key: 'status', label: 'Status', options: [{ value: 'active', label: 'Active' }, { value: 'expired', label: 'Expired' }, { value: 'revoked', label: 'Revoked' }] },
  ],
  incident_activated: [
    { key: 'name', label: 'Incident Name' },
    { key: 'incidentType', label: 'Incident Type', options: [{ value: 'hurricane', label: 'Hurricane' }, { value: 'tornado', label: 'Tornado' }, { value: 'flood', label: 'Flood' }, { value: 'earthquake', label: 'Earthquake' }, { value: 'wildfire', label: 'Wildfire' }, { value: 'hazmat', label: 'HazMat' }, { value: 'other', label: 'Other' }] },
    { key: 'status', label: 'Status', options: [{ value: 'active', label: 'Active' }, { value: 'monitoring', label: 'Monitoring' }, { value: 'closed', label: 'Closed' }] },
    { key: 'location', label: 'Location' },
    { key: 'commander', label: 'Incident Commander' },
    { key: 'description', label: 'Description' },
  ],
  new_pa_project: [
    { key: 'projectTitle', label: 'Project Title' },
    { key: 'projectNumber', label: 'Project Number' },
    { key: 'category', label: 'Category', options: [{ value: 'A', label: 'A — Debris Removal' }, { value: 'B', label: 'B — Emergency Measures' }, { value: 'C', label: 'C — Roads & Bridges' }, { value: 'D', label: 'D — Water Control' }, { value: 'E', label: 'E — Buildings & Equipment' }, { value: 'F', label: 'F — Utilities' }, { value: 'G', label: 'G — Parks & Recreation' }] },
    { key: 'projectSize', label: 'Project Size', options: [{ value: 'small', label: 'Small' }, { value: 'large', label: 'Large' }] },
    { key: 'status', label: 'Status' },
    { key: 'phase', label: 'Phase' },
    { key: 'estimatedCost', label: 'Estimated Cost' },
    { key: 'projectCounty', label: 'County' },
  ],
  rfr_approved: [
    { key: 'requestNumber', label: 'RFR Number' },
    { key: 'projectNumber', label: 'Project Number' },
    { key: 'projectTitle', label: 'Project Title' },
    { key: 'applicant', label: 'Applicant' },
    { key: 'requestedAmount', label: 'Requested Amount' },
    { key: 'approvedAmount', label: 'Approved Amount' },
    { key: 'federalShare', label: 'Federal Share' },
    { key: 'applicantMatch', label: 'Applicant Match' },
    { key: 'periodStart', label: 'Period Start' },
    { key: 'periodEnd', label: 'Period End' },
    { key: 'costBreakdown', label: 'Cost Breakdown' },
    { key: 'reviewedBy', label: 'Reviewed By' },
    { key: 'reviewNotes', label: 'Review Notes' },
    { key: 'status', label: 'Status' },
    { key: 'link', label: 'Link to RFR' },
  ],
  new_hmgp_subapp: [
    { key: 'subappNumber', label: 'Subapp Number' },
    { key: 'subappTitle', label: 'Title' },
    { key: 'projectType', label: 'Mitigation Type' },
    { key: 'applicant', label: 'Applicant' },
    { key: 'estimatedCost', label: 'Estimated Cost' },
    { key: 'bcaRatio', label: 'BCA Ratio' },
    { key: 'status', label: 'Status' },
  ],
  hmgp_subapp_forwarded: [
    { key: 'subappNumber', label: 'Subapp Number' },
    { key: 'subappTitle', label: 'Title' },
    { key: 'projectType', label: 'Mitigation Type' },
    { key: 'applicant', label: 'Applicant' },
    { key: 'estimatedCost', label: 'Estimated Cost' },
    { key: 'bcaRatio', label: 'BCA Ratio' },
    { key: 'statePriorityRank', label: 'Priority Rank' },
  ],
  hmgp_project_awarded: [
    { key: 'projectNumber', label: 'Project Number' },
    { key: 'projectTitle', label: 'Project Title' },
    { key: 'projectType', label: 'Mitigation Type' },
    { key: 'applicant', label: 'Applicant' },
    { key: 'awardAmount', label: 'Award Amount' },
    { key: 'phase', label: 'Phase' },
    { key: 'status', label: 'Status' },
  ],
  new_perimeter_closure: [
    { key: 'name', label: 'Closure Name' },
    { key: 'roadType', label: 'Road Type' },
    { key: 'priority', label: 'Priority' },
    { key: 'direction', label: 'Direction' },
  ],
  new_geoops_zone: [
    { key: 'name', label: 'Zone Name' },
    { key: 'zoneType', label: 'Zone Type' },
    { key: 'status', label: 'Zone Status' },
    { key: 'incidentId', label: 'Incident ID' },
  ],
  new_debris_request: [
    { key: 'zoneName', label: 'Zone Name' },
    { key: 'zoneCode', label: 'Zone Code' },
    { key: 'status', label: 'Status', options: [{ value: 'pending', label: 'Pending' }, { value: 'assigned', label: 'Assigned' }, { value: 'in_progress', label: 'In Progress' }, { value: 'completed', label: 'Completed' }] },
    { key: 'estimatedCubicYards', label: 'Estimated Cubic Yards' },
    { key: 'debrisTypes', label: 'Debris Types' },
  ],
  training_expired: [
    { key: 'trainingName', label: 'Training Name' },
    { key: 'userName', label: 'User Name' },
    { key: 'expiresDate', label: 'Expires Date' },
    { key: 'completedDate', label: 'Completed Date' },
  ],
  calldown_low_response: [
    { key: 'personName', label: 'Person Name' },
    { key: 'personEmail', label: 'Person Email' },
    { key: 'responseStatus', label: 'Response Status', options: [{ value: 'contacted', label: 'Contacted' }, { value: 'no_answer', label: 'No Answer' }, { value: 'left_message', label: 'Left Message' }, { value: 'confirmed', label: 'Confirmed' }, { value: 'unavailable', label: 'Unavailable' }] },
    { key: 'methodUsed', label: 'Method Used', options: [{ value: 'phone', label: 'Phone' }, { value: 'text', label: 'Text' }, { value: 'email', label: 'Email' }, { value: 'radio', label: 'Radio' }, { value: 'in_person', label: 'In Person' }] },
  ],
};

export type WorkflowCategory = 'approval' | 'review' | 'routing' | 'notification' | 'custom';

export type WorkflowPriority = 'low' | 'normal' | 'high' | 'critical';

export type NotifyChannel = 'email' | 'sms';

export type EscalationAction = 'reassign' | 'notify_admin' | 'auto_approve' | 'auto_complete';

// ── Document checklist (per step) ──────────────────────────

export interface DocumentCheckItem {
  key: string;                           // unique id within step (e.g., 'sow', 'ddd', 'photos')
  label: string;                         // display name
  required: boolean;
  description?: string;                  // guidance text
}

export interface DocumentSubmission {
  key: string;                           // matches DocumentCheckItem.key
  completed: boolean;
  completedBy?: string;
  completedAt?: string;
  notes?: string;
}

// ── Step Definition (stored in template steps_json) ─────────

export interface WorkflowStepDef {
  name: string;
  type: WorkflowStepType;
  assigneeType: AssigneeType;
  assignee: string;                      // email, role name, or field key
  notifyChannels?: NotifyChannel[];
  escalation?: {
    afterHours: number;
    action: EscalationAction;
    target?: string;                     // reassign target email/role
  };
  onApprove?: string;                    // 'next' | 'goto:N' | 'end'
  onReject?: string;                     // 'goto:N' | 'end' | 'cancel'
  instructions?: string;                 // guidance shown when step is active
  documentChecklist?: DocumentCheckItem[]; // required documents for this step
  config?: {
    targetTable?: string;                // for create_record
    targetField?: string;                // for update_field
    targetValue?: string;                // for update_field
    condition?: StepCondition | string;   // for conditional: structured condition object (or legacy JSON string)
    conditionTrueStep?: number;
    conditionFalseStep?: number;
    messageTemplate?: string;            // for notification
    message?: string;
  };
}

// ── Persisted Records ───────────────────────────────────────

export interface WorkflowTemplate {
  id: number; Rid: number;
  name: string;
  description: string;
  module: string;                        // pagrants | impact | debris | *
  triggerTable: string;
  triggerField: string;
  triggerValue: string;
  triggerType: string;
  stepsJson: string;                     // JSON.stringify(WorkflowStepDef[])
  active: string;                        // 'yes' | 'no'
  category: string;
  priority: string;
  creatorId: string;
  notes: string;
}

export interface WorkflowInstance {
  id: number; Rid: number;
  templateId: string;
  templateName: string;
  sourceModule: string;
  sourceTable: string;
  sourceRecordId: string;
  sourceRecordLabel: string;
  currentStepIndex: string;
  totalSteps: string;
  status: string;
  priority: string;
  startedAt: string;
  completedAt: string;
  startedBy: string;
  contextJson: string;                   // JSON context passed between steps
  notes: string;
}

export interface WorkflowStep {
  id: number; Rid: number;
  instanceId: string;
  stepIndex: string;
  stepName: string;
  stepType: string;
  assigneeType: string;
  assignee: string;
  assigneeName: string;
  status: string;
  dueDate: string;
  completedBy: string;
  completedAt: string;
  outcome: string;
  comments: string;
  notifyChannels: string;
  escalationHours: string;
  escalationAction: string;
  escalationTarget: string;
  configJson: string;
  signatureData: string;
  notes: string;
}

// ── UI helper types ─────────────────────────────────────────

export interface WorkflowInboxItem {
  step: WorkflowStep;
  instance: WorkflowInstance;
  template: WorkflowTemplate;
}

export const STEP_TYPE_LABELS: Record<WorkflowStepType, string> = {
  approval: 'Approval',
  review: 'Review',
  task: 'Task',
  signature: 'Signature',
  notification: 'Notification',
  create_record: 'Create Record',
  update_field: 'Update Field',
  conditional: 'Conditional',
};

export const STEP_TYPE_COLORS: Record<WorkflowStepType, string> = {
  approval: '#7C3AED',
  review: '#0284C7',
  task: '#D97706',
  signature: '#059669',
  notification: '#6366F1',
  create_record: '#16A34A',
  update_field: '#92400E',
  conditional: '#64748B',
};

export const INSTANCE_STATUS_COLORS: Record<InstanceStatus, string> = {
  active: '#0284C7',
  completed: '#059669',
  cancelled: '#64748B',
  escalated: '#DC2626',
  paused: '#D97706',
};

export const PRIORITY_COLORS: Record<WorkflowPriority, string> = {
  low: '#64748B',
  normal: '#0284C7',
  high: '#D97706',
  critical: '#DC2626',
};

// ── Pre-built workflow templates ────────────────────────────

export const PRESET_WORKFLOWS: {
  name: string;
  description: string;
  category: WorkflowCategory;
  module: string;
  trigger: FriendlyTrigger;
  steps: WorkflowStepDef[];
}[] = [
  {
    name: 'Damage Assessment Review',
    description: 'Routes damage assessments through review and approval before forwarding to PA Grants.',
    category: 'approval', module: 'impact', trigger: 'new_damage_assessment',
    steps: [
      { name: 'Initial Review', type: 'review', assigneeType: 'role', assignee: 'editor', instructions: 'Review the damage assessment for completeness and accuracy.', notifyChannels: [], config: {} },
      { name: 'Supervisor Approval', type: 'approval', assigneeType: 'role', assignee: 'admin', instructions: 'Approve or reject the damage assessment.', onReject: 'goto:0', notifyChannels: [], config: {} },
      { name: 'Notify Submitter', type: 'notification', assigneeType: 'creator', assignee: '', instructions: '', notifyChannels: ['email'], config: { message: 'Your damage assessment has been approved.' } },
    ],
  },
  {
    name: 'Volunteer Onboarding',
    description: 'Processes new volunteer applications through review, orientation assignment, and activation.',
    category: 'routing', module: 'volunteer', trigger: 'new_volunteer',
    steps: [
      { name: 'Application Review', type: 'review', assigneeType: 'role', assignee: 'editor', instructions: 'Review volunteer application for completeness.', notifyChannels: [], config: {} },
      { name: 'Background Check', type: 'task', assigneeType: 'role', assignee: 'admin', instructions: 'Run background check and verify references.', notifyChannels: [], config: {} },
      { name: 'Assign Orientation', type: 'task', assigneeType: 'role', assignee: 'editor', instructions: 'Schedule orientation training session.', notifyChannels: [], config: {} },
      { name: 'Welcome Notification', type: 'notification', assigneeType: 'creator', assignee: '', instructions: '', notifyChannels: ['email'], config: { message: 'Welcome! Your volunteer application has been approved.' } },
    ],
  },
  {
    name: 'Business Registration Approval',
    description: 'Reviews new business registrations, creates accounts, and onboards to MainStreet portal.',
    category: 'approval', module: 'mainstreet', trigger: 'business_registered',
    steps: [
      { name: 'Review Registration', type: 'review', assigneeType: 'role', assignee: 'editor', instructions: 'Verify business information and legitimacy.', notifyChannels: [], config: {} },
      { name: 'Director Approval', type: 'approval', assigneeType: 'role', assignee: 'admin', instructions: 'Approve business for portal access.', notifyChannels: [], config: {} },
      { name: 'Create Account', type: 'task', assigneeType: 'role', assignee: 'admin', instructions: 'Create Ignatius login and assign biz_owner role.', notifyChannels: [], config: {} },
      { name: 'Welcome Email', type: 'notification', assigneeType: 'creator', assignee: '', instructions: '', notifyChannels: ['email'], config: { message: 'Your business has been approved for portal access.' } },
    ],
  },
  {
    name: 'Supply Restock Request',
    description: 'Routes supply restock requests through approval and fulfillment.',
    category: 'approval', module: 'shelter', trigger: 'supply_low_stock',
    steps: [
      { name: 'Request Review', type: 'review', assigneeType: 'role', assignee: 'admin', instructions: 'Review and approve the restock request.', notifyChannels: [], config: {} },
      { name: 'Procurement', type: 'task', assigneeType: 'role', assignee: 'editor', instructions: 'Order supplies from vendor.', notifyChannels: [], config: {} },
      { name: 'Delivery Confirmation', type: 'task', assigneeType: 'creator', assignee: '', instructions: 'Confirm supplies received and update inventory.', notifyChannels: [], config: {} },
    ],
  },
  {
    name: 'Re-Entry Pass Authorization',
    description: 'Reviews and approves re-entry pass requests for businesses in affected areas.',
    category: 'approval', module: 'mainstreet', trigger: 'reentry_requested',
    steps: [
      { name: 'Verify Business', type: 'review', assigneeType: 'role', assignee: 'editor', instructions: 'Verify business is in the authorized re-entry zone.', notifyChannels: [], config: {} },
      { name: 'Safety Clearance', type: 'approval', assigneeType: 'role', assignee: 'admin', instructions: 'Confirm area is safe for re-entry.', notifyChannels: [], config: {} },
      { name: 'Issue Pass', type: 'task', assigneeType: 'role', assignee: 'editor', instructions: 'Generate and issue the re-entry pass.', notifyChannels: [], config: {} },
      { name: 'Notify Business', type: 'notification', assigneeType: 'creator', assignee: '', instructions: '', notifyChannels: ['email'], config: { message: 'Your re-entry pass has been issued.' } },
    ],
  },
  {
    name: 'Incident After-Action Review',
    description: 'Collects after-action feedback, routes through review, and generates improvement plan.',
    category: 'review', module: 'hotwash', trigger: 'manual',
    steps: [
      { name: 'Distribute Survey', type: 'notification', assigneeType: 'role', assignee: 'editor', instructions: '', notifyChannels: ['email'], config: { message: 'Please complete the after-action survey.' } },
      { name: 'Compile Results', type: 'task', assigneeType: 'role', assignee: 'editor', instructions: 'Compile survey results and draft improvement plan.', notifyChannels: [], config: {} },
      { name: 'Director Review', type: 'approval', assigneeType: 'role', assignee: 'admin', instructions: 'Review and approve the improvement plan.', notifyChannels: [], config: {} },
    ],
  },
];
